/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Translation Memory (TM) Engine DTOs (Task 8.2.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { z } from '@hono/zod-openapi';

/** BCP-47 style language code (e.g. "en", "vi", "zh-CN"). */
const LanguageCodeSchema = z
  .string()
  .regex(/^[a-z]{2,3}(-[A-Za-z0-9]{2,8})*$/, 'Invalid language code')
  .openapi({ description: 'BCP-47 language code', example: 'en' });

// ============================================================================
// 1. TM LOOKUP
// ============================================================================

export const TmLookupRequestSchema = z
  .object({
    sourceLang: LanguageCodeSchema,
    targetLang: LanguageCodeSchema,
    sourceText: z.string().trim().min(1).max(5000).openapi({
      description: 'Sentence text to look up in Translation Memory',
      example: 'Welcome to Sophia Platform.',
    }),
  })
  .openapi('TmLookupRequest');

export type TmLookupRequestDto = z.infer<typeof TmLookupRequestSchema>;

export const TmLookupResponseSchema = z
  .object({
    matched: z.boolean().openapi({ description: 'True if a Translation Memory entry was found' }),
    targetText: z.string().nullable().openapi({ description: 'Translated target text if matched' }),
    matchType: z.enum(['exact', 'fuzzy', 'mt']).nullable().openapi({ description: 'Match classification', example: 'exact' }),
    similarityScore: z.number().int().min(0).max(100).openapi({ description: 'Similarity percentage score (100 for exact match)' }),
    sourceHash: z.string().openapi({ description: 'SHA-256 hash of normalized source string' }),
    hitCount: z.number().int().nullable().openapi({ description: 'Cumulative times this TM entry has been used' }),
    tmEntryId: z.string().nullable().openapi({ description: 'Primary key of the matching translation_memories row' }),
  })
  .openapi('TmLookupResponse');

export type TmLookupResponseDto = z.infer<typeof TmLookupResponseSchema>;

// ============================================================================
// 2. TM ENTRY CRUD
// ============================================================================

export const CreateTmEntrySchema = z
  .object({
    sourceLang: LanguageCodeSchema,
    targetLang: LanguageCodeSchema,
    sourceText: z.string().trim().min(1).max(10000).openapi({ example: 'Hello world.' }),
    targetText: z.string().trim().min(1).max(10000).openapi({ example: 'Xin chào thế giới.' }),
  })
  .openapi('CreateTmEntry');

export type CreateTmEntryDto = z.infer<typeof CreateTmEntrySchema>;

export const BatchCreateTmEntriesSchema = z
  .object({
    sourceLang: LanguageCodeSchema,
    targetLang: LanguageCodeSchema,
    entries: z
      .array(
        z.object({
          sourceText: z.string().trim().min(1),
          targetText: z.string().trim().min(1),
        }),
      )
      .min(1)
      .max(500)
      .openapi({ description: 'Array of bilingual translation pairs' }),
  })
  .openapi('BatchCreateTmEntries');

export type BatchCreateTmEntriesDto = z.infer<typeof BatchCreateTmEntriesSchema>;

export const TmEntryResponseSchema = z
  .object({
    id: z.string().openapi({ example: 'tm_01h8abc123' }),
    workspaceId: z.string(),
    sourceLang: z.string().openapi({ example: 'en' }),
    targetLang: z.string().openapi({ example: 'vi' }),
    sourceHash: z.string(),
    sourceText: z.string(),
    targetText: z.string(),
    hitCount: z.number().int().nonnegative(),
    createdAt: z.string().openapi({ example: '2026-10-23T10:00:00.000Z' }),
  })
  .openapi('TmEntryResponse');

export type TmEntryResponseDto = z.infer<typeof TmEntryResponseSchema>;

export const ListTmEntriesQuerySchema = z
  .object({
    sourceLang: z.string().min(1).optional(),
    targetLang: z.string().min(1).optional(),
    search: z.string().trim().min(1).max(100).optional(),
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(20),
  })
  .openapi('ListTmEntriesQuery');

export type ListTmEntriesQueryDto = z.infer<typeof ListTmEntriesQuerySchema>;

export const ListTmEntriesResponseSchema = z
  .object({
    items: z.array(TmEntryResponseSchema),
    pagination: z.object({
      page: z.number().int(),
      limit: z.number().int(),
      total: z.number().int(),
      totalPages: z.number().int(),
    }),
  })
  .openapi('ListTmEntriesResponse');

export type ListTmEntriesResponseDto = z.infer<typeof ListTmEntriesResponseSchema>;

// ============================================================================
// 3. APPLY TM TO PROJECT (Task 8.2.1 Batch Exact Match Execution)
// ============================================================================

export const ApplyTmToProjectSchema = z
  .object({
    autoReviewExactMatch: z.boolean().default(false).optional().openapi({
      description: 'If true, automatically marks 100% exact matches as reviewed (status: reviewed, isReviewed: true)',
      example: false,
    }),
    batchSize: z.coerce.number().int().min(1).max(100).default(50).optional().openapi({
      description: 'Batch size for D1 transaction batching',
      example: 50,
    }),
  })
  .openapi('ApplyTmToProject');

export type ApplyTmToProjectDto = z.infer<typeof ApplyTmToProjectSchema>;

export const ApplyTmToProjectResponseSchema = z
  .object({
    translationId: z.string(),
    totalChunks: z.number().int().nonnegative(),
    exactMatchedChunks: z.number().int().nonnegative().openapi({ description: 'Number of chunks populated with 100% exact match' }),
    autoReviewedChunks: z.number().int().nonnegative().openapi({ description: 'Number of chunks marked as reviewed' }),
    tokensSavedEstimate: z.number().int().nonnegative().openapi({ description: 'Estimated LLM tokens saved by skipping AI translation' }),
    progressPercent: z.number().min(0).max(100).openapi({ description: 'Updated completion progress percentage' }),
  })
  .openapi('ApplyTmToProjectResponse');

export type ApplyTmToProjectResponseDto = z.infer<typeof ApplyTmToProjectResponseSchema>;

// ============================================================================
// 4. FUZZY MATCH LOOKUP (Task 8.2.2 Two-Stage Matching 70% - 99%)
// ============================================================================

export const DiffTokenSchema = z
  .object({
    type: z.enum(['added', 'removed', 'unchanged']).openapi({
      description: 'Diff token type indicating additions, deletions, or unchanged text relative to TM baseline',
      example: 'unchanged',
    }),
    text: z.string().openapi({ example: 'Welcome to Sophia Platform ' }),
  })
  .openapi('DiffToken');

export type DiffTokenDto = z.infer<typeof DiffTokenSchema>;

export const FuzzyMatchItemSchema = z
  .object({
    id: z.string().openapi({ example: 'tm_01jh8...' }),
    sourceText: z.string().openapi({ example: 'Welcome to Sophia Platform cloud edition.' }),
    targetText: z.string().openapi({ example: 'Chào mừng bạn đến với Sophia Platform bản đám mây.' }),
    similarityScore: z.number().int().min(50).max(99).openapi({
      description: 'Levenshtein similarity percentage (70% - 99%)',
      example: 85,
    }),
    jaccardScore: z.number().min(0).max(1).openapi({
      description: 'Character trigrams Jaccard index',
      example: 0.75,
    }),
    diffTokens: z.array(DiffTokenSchema).openapi({
      description: 'Myers inline diff markup highlighting changes',
    }),
    hitCount: z.number().int().nonnegative().optional(),
  })
  .openapi('FuzzyMatchItem');

export type FuzzyMatchItemDto = z.infer<typeof FuzzyMatchItemSchema>;

export const TmFuzzyLookupRequestSchema = z
  .object({
    sourceText: z.string().trim().min(1).max(5000).openapi({
      example: 'Welcome to Sophia Platform edge edition.',
    }),
    sourceLang: z.string().min(2).max(10).openapi({ example: 'en' }),
    targetLang: z.string().min(2).max(10).openapi({ example: 'vi' }),
    threshold: z.coerce.number().int().min(50).max(99).default(70).optional().openapi({
      description: 'Minimum similarity score threshold (default: 70)',
      example: 70,
    }),
    limit: z.coerce.number().int().min(1).max(10).default(5).optional().openapi({
      description: 'Maximum number of fuzzy suggestions to return (default: 5)',
      example: 5,
    }),
  })
  .openapi('TmFuzzyLookupRequest');

export type TmFuzzyLookupRequestDto = z.infer<typeof TmFuzzyLookupRequestSchema>;

export const TmFuzzyLookupResponseSchema = z
  .object({
    queryText: z.string(),
    sourceLang: z.string(),
    targetLang: z.string(),
    threshold: z.number().int(),
    count: z.number().int().nonnegative(),
    matches: z.array(FuzzyMatchItemSchema),
  })
  .openapi('TmFuzzyLookupResponse');

export type TmFuzzyLookupResponseDto = z.infer<typeof TmFuzzyLookupResponseSchema>;

// ============================================================================
// 6. IMPORT TMX MEMORY (Task 8.5.3)
// ============================================================================

export const ImportTmxRequestSchema = z
  .object({
    sourceLang: z.string().min(2).max(10).optional().openapi({
      description: 'Optional filter for source language (e.g. en, en-US). If omitted, inferred from TMX header.',
      example: 'en',
    }),
    targetLang: z.string().min(2).max(10).optional().openapi({
      description: 'Optional filter for target language (e.g. vi, vi-VN). If omitted, inferred from TMX translation units.',
      example: 'vi',
    }),
    tmxContent: z.string().min(1).optional().openapi({
      description: 'Direct XML string content of the TMX 1.4 document.',
    }),
    r2Key: z.string().min(1).optional().openapi({
      description: 'Cloudflare R2 object key containing large TMX file (> 10MB) uploaded via presigned URL.',
      example: 'tmx-uploads/workspace_123/legacy_tm.tmx',
    }),
    overwriteExisting: z.boolean().default(true).optional().openapi({
      description: 'Whether to overwrite targetText when exact sourceHash match exists in workspace TM. Default: true.',
      example: true,
    }),
    stripInlineTags: z.boolean().default(true).optional().openapi({
      description: 'Whether to strip inline TMX markup tags (<bpt>, <ept>, etc.) while preserving clean text. Default: true.',
      example: true,
    }),
  })
  .openapi('ImportTmxRequest');

export type ImportTmxRequestDto = z.infer<typeof ImportTmxRequestSchema>;

export const ImportTmxResponseSchema = z
  .object({
    importedCount: z.number().int().nonnegative().openapi({
      description: 'Number of newly inserted Translation Memory pairs in D1',
      example: 120,
    }),
    updatedCount: z.number().int().nonnegative().openapi({
      description: 'Number of existing Translation Memory pairs updated (or skipped on duplicate conflict)',
      example: 15,
    }),
    skippedCount: z.number().int().nonnegative().openapi({
      description: 'Number of translation units skipped due to missing language pair or empty segments',
      example: 2,
    }),
    totalTuFound: z.number().int().nonnegative().openapi({
      description: 'Total number of <tu> tags detected in the TMX file',
      example: 137,
    }),
    sourceLang: z.string().openapi({ description: 'Primary source language of imported units', example: 'en' }),
    targetLang: z.string().openapi({ description: 'Primary target language of imported units', example: 'vi' }),
    executionTimeMs: z.number().int().nonnegative().openapi({
      description: 'Total execution time in milliseconds',
      example: 45,
    }),
  })
  .openapi('ImportTmxResponse');

export type ImportTmxResponseDto = z.infer<typeof ImportTmxResponseSchema>;
